// Libs
import classNames from 'classnames/bind';
import { Box, Checkbox, FormControlLabel, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLoadScript } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { BaseAvatarGroup, BaseLabelValueField, BaseMap } from '~/components/common';
// Others
import { formatDateByTimeZone, formatPhoneNumber, formatTitleCase, getFullName } from '~/utils/helper/common';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { googleMapApiKey } from '~/utils/constants/env';
import { DateFormatEnum, UnitEstimateTypeEnum } from '~/utils/enum/common';
import { selectEstimateDetail } from '~/thunks/estimate/estimateSlice';
// Styles, images, icons
import styles from './EstimateSummary.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const EstimateSummary = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  //#endregion Declare Hook

  //#region Selector
  const estimateDetails = useSelector(selectEstimateDetail);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function

  //#endregion Handle Function

  return (
    <Box component={'div'} id='estimateSummaryPage' className={cx('container')}>
      <Grid2 container spacing={'24px'} component={'div'} className={cx('body')}>
        <Grid2 size={6}>
          <BaseLabelValueField
            label={t('tab_summary_primary_contact')}
            value={
              <Stack spacing={'8px'}>
                <Typography component={'p'} className={cx('textValue')}>
                  {getFullName({
                    firstName: estimateDetails?.customer?.firstName,
                    lastName: estimateDetails?.customer?.lastName,
                  })}
                </Typography>

                {estimateDetails?.customer?.phoneNumber && (
                  <Typography component={'p'} className={cx('textValue')}>
                    {formatPhoneNumber(estimateDetails?.customer?.phoneNumber)}
                  </Typography>
                )}

                {estimateDetails?.customer?.email && (
                  <Typography component={'p'} className={cx('textValue')}>
                    {estimateDetails?.customer?.email || EMPTY_STRING}
                  </Typography>
                )}
              </Stack>
            }
          />
          <BaseLabelValueField
            label={t('tab_summary_schedule')}
            value={
              <Stack spacing={'8px'}>
                <Box component={'div'} className={cx('viewFrom')}>
                  <Typography component={'p'} className={cx('textValueFrom')}>
                    {t('tab_summary_from_label')}
                  </Typography>
                  <Typography component={'p'} className={cx('textValue')}>
                    {estimateDetails?.schedule?.from
                      ? formatDateByTimeZone(
                          estimateDetails.schedule.from,
                          DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                          estimateDetails?.timeZone
                        )
                      : EMPTY_STRING}
                  </Typography>
                </Box>
                <Box component={'div'} className={cx('viewFrom')}>
                  <Typography component={'p'} className={cx('textValueFrom')}>
                    {t('tab_summary_to_label')}
                  </Typography>
                  <Typography component={'p'} className={cx('textValue')}>
                    {estimateDetails?.schedule?.to
                      ? formatDateByTimeZone(
                          estimateDetails.schedule.to,
                          DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                          estimateDetails?.timeZone
                        )
                      : EMPTY_STRING}
                  </Typography>
                </Box>

                {estimateDetails?.timeZone && (
                  <Typography component={'p'} className={cx('textValue')}>
                    {estimateDetails?.timeZone}
                  </Typography>
                )}

                <BaseAvatarGroup avatars={estimateDetails?.assignees || []} borderColor='#FFF' max={5} />
              </Stack>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_estimate_tag')}
            value={
              <>
                {estimateDetails?.tags &&
                  estimateDetails?.tags?.length > DEFAULT_NUMBER_ZERO &&
                  estimateDetails.tags?.map((item) => item?.name).join(', ')}
              </>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_lead_source')}
            value={formatTitleCase(estimateDetails?.leadSource?.name || EMPTY_STRING)}
          />

          <BaseLabelValueField
            label={t('tab_summary_estimate_fields')}
            value={
              <Stack spacing={'8px'}>
                {estimateDetails?.type && (
                  <Typography component={'div'} className={cx('text')}>
                    {formatTitleCase(estimateDetails?.type || EMPTY_STRING)}
                  </Typography>
                )}

                {estimateDetails?.unit && (
                  <Typography component={'div'} className={cx('text')}>
                    {estimateDetails?.unit !== UnitEstimateTypeEnum.Plumbing
                      ? estimateDetails?.unit
                      : formatTitleCase(estimateDetails?.unit || EMPTY_STRING)}
                  </Typography>
                )}

                <FormControlLabel
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  className={cx('formControlLabel')}
                  control={
                    <Checkbox checked={estimateDetails?.callback} disabled className={cx('checkbox')} />
                  }
                  label={t('tab_summary_call_back')}
                />
              </Stack>
            }
          />
        </Grid2>

        <Grid2 container direction={'column'} size={6} spacing={0}>
          <Stack className={cx('mapContainer')}>
            <BaseLabelValueField
              label={t('tab_summary_address')}
              value={estimateDetails?.address}
              bordered={false}
              labelSize={'auto'}
            />

            <Stack flex={1} className={cx('map')}>
              {isLoaded && (
                <BaseMap
                  {...(estimateDetails?.latitude &&
                    estimateDetails?.longitude && {
                      position: {
                        lat: estimateDetails?.latitude,
                        lng: estimateDetails?.longitude,
                      },
                    })}
                />
              )}
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default EstimateSummary;
